import React from 'react';
import { SpeechEntry } from '../hooks/useSpeechBubbles';

interface PointLike { id: number; x: number; y: number; }
interface ViewportLike { x: number; y: number; zoom: number; }

interface Props {
  speeches   : Map<number, SpeechEntry>;
  points     : PointLike[];
  viewport   : ViewportLike;
  windowSize : { width: number; height: number };
}

const BUBBLE_W = 188;
const BUBBLE_H = 50;   // approximate height — only used for anti-overlap stacking
const GAP      = 4;

/**
 * Renders speech bubbles (comic balloons) above pixels.
 *
 * Anchor strategy — the inline `top` is the target Y of the arrow tip, which
 * is the pixel's top edge on screen:
 *   pixel_top_y = (vp.y − point.y) · zoom + H/2
 *
 * CSS does the lift: `transform: translate(-50%, calc(-100% − 7px))` shifts the
 * bubble up by its own rendered height plus 7px (visible portion of the ::after
 * arrow below the bubble's border box). This way the tip lands on the anchor
 * regardless of the bubble's actual rendered height (which varies with text
 * content) or zoom.
 *
 * Anti-overlap: bubbles that would collide horizontally are shifted upward
 * (anchor moved up) so they stack instead of overlapping.
 */
export const SpeechBubbles: React.FC<Props> = ({
  speeches, points, viewport, windowSize,
}) => {
  const { width: W, height: H } = windowSize;
  const { x: vpX, y: vpY, zoom } = viewport;

  type Item = {
    pointId: number; text: string; style: SpeechEntry['style']; ts: number;
    cx: number; bx: number; anchorY: number;
  };

  const items: Item[] = [];
  for (const [pointId, speech] of Array.from(speeches.entries())) {
    const pt = points.find(p => p.id === pointId);
    if (!pt) continue;
    const cx        = (pt.x - vpX) * zoom + W / 2 + zoom / 2;
    const pixelTopY = (vpY - pt.y) * zoom + H / 2;
    items.push({
      pointId,
      text    : speech.text,
      style   : speech.style,
      ts      : speech.ts,
      cx,
      bx      : cx - BUBBLE_W / 2,
      anchorY : pixelTopY,
    });
  }

  // Sort top-to-bottom so earlier (visually higher) items are processed first.
  items.sort((a, b) => a.anchorY - b.anchorY);

  // Resolve vertical overlaps: push later bubble's anchor up to stack above.
  const adj = items.map(item => ({ ...item, adjAnchorY: item.anchorY }));
  for (let i = 1; i < adj.length; i++) {
    for (let j = 0; j < i; j++) {
      const a = adj[j];
      const b = adj[i];
      const hOverlap = b.bx < a.bx + BUBBLE_W + GAP && b.bx + BUBBLE_W + GAP > a.bx;
      if (hOverlap && b.adjAnchorY < a.adjAnchorY + BUBBLE_H + GAP) {
        adj[i] = { ...b, adjAnchorY: a.adjAnchorY - BUBBLE_H - GAP };
      }
    }
  }

  return (
    <>
      {adj.map(({ pointId, text, style, ts, cx, adjAnchorY }) => (
        <div
          key={`${pointId}-${ts}`}
          className={`speech-bubble speech-bubble--${style}`}
          style={{ left: cx, top: adjAnchorY }}
        >
          <span className="speech-bubble__text">{text}</span>
        </div>
      ))}
    </>
  );
};
